import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ICreateItemRequest,
  ICreateItemResult,
  IItemCreateResponse,
  IItemResolution,
  ILookupAttribute,
  ILookupCategory,
  ILookupExwPoint,
  ILookupManufacturer,
  ItemResponse
} from './item-create.models';

/**
 * Client for the hubx-item-domain v2 API (HUBX-8385). Deliberately isolated from
 * VendorService/legacy endpoints — this is the only place in the frontend that
 * talks to itemDomainBaseUrl for the Create feature.
 *
 * Lookups (design doc §8.4): cached in memory per tab via shareReplay(1) — no
 * localStorage/sessionStorage. The server sends strong ETags + Cache-Control:
 * private, max-age=3600 and honors If-None-Match, so revalidation beyond this
 * in-memory cache is the browser HTTP cache's job, not ours. shareReplay does
 * NOT cache errors (RxJS 7 resets on error), so a failed lookup is retried on
 * the next subscription.
 *
 * Auth: AuthInterceptor attaches Bearer + X-Impersonation-Id to every request
 * regardless of origin, so nothing extra is needed here.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemCreateService {
  private readonly baseUrl = environment.itemDomainBaseUrl;
  private readonly lookupCache = new Map<string, Observable<unknown>>();

  constructor(private http: HttpClient) { }

  getManufacturers(): Observable<ILookupManufacturer[]> {
    return this.lookup<ILookupManufacturer[]>('lookups/manufacturers');
  }

  getCategories(): Observable<ILookupCategory[]> {
    return this.lookup<ILookupCategory[]>('lookups/categories');
  }

  getAttributes(): Observable<ILookupAttribute[]> {
    return this.lookup<ILookupAttribute[]>('lookups/attributes');
  }

  getExwPoints(): Observable<ILookupExwPoint[]> {
    return this.lookup<ILookupExwPoint[]>('lookups/exw-points');
  }

  /**
   * POST /items → 201 + Location: /items/{id} + the write-ack body.
   * Emits the new item id (last segment of Location), the raw Location, and the
   * body's `warnings` (HUBX-8428) — non-blocking advisories on the successful
   * write, empty and never null on the ordinary path. Errors pass through as
   * HttpErrorResponse; err.error is IItemProblemDetails and the caller routes by
   * its stable `code`.
   */
  createItem(request: ICreateItemRequest): Observable<ICreateItemResult> {
    return this.http.post<IItemCreateResponse>(this.baseUrl + 'items', request, { observe: 'response' }).pipe(
      map(response => {
        const location = response.headers.get('Location') || '';
        const id = location.split('/').filter(segment => !!segment).pop() || '';
        // Defensive on warnings only: the id keeps coming from Location (unchanged),
        // and a body that somehow arrived empty must not break a create that the
        // server already committed.
        return { id, location, warnings: response.body?.warnings ?? [] };
      })
    );
  }

  /**
   * GET /items/{id} → the canonical item resource, used to seed the Create modal
   * for Clone (§4.3). Not cached: unlike the lookups this is per-item, mutable
   * state, so every Clone reads the current values fresh.
   */
  getItem(id: string): Observable<ItemResponse> {
    return this.http.get<ItemResponse>(this.baseUrl + 'items/' + id);
  }

  /**
   * GET /items/resolutions?mpn={mpn} → whether the MPN already resolves to an
   * existing item and, if so, which category that item sits under (HUBX-8430).
   * Feeds the pre-submit category nudge; always 200, so a non-match arrives as
   * `resolved: false` rather than an error.
   *
   * Not cached, for the same reason as getItem(): it is per-MPN, mutable state —
   * another vendor's create can change the answer between two calls. Callers must
   * pass a non-blank MPN; the endpoint answers 400 to a blank one.
   */
  resolveByMpn(mpn: string): Observable<IItemResolution> {
    return this.http.get<IItemResolution>(this.baseUrl + 'items/resolutions', {
      params: new HttpParams().set('mpn', mpn)
    });
  }

  private lookup<T>(path: string): Observable<T> {
    let cached = this.lookupCache.get(path) as Observable<T>;
    if (!cached) {
      cached = this.http.get<T>(this.baseUrl + path).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.lookupCache.set(path, cached);
    }
    return cached;
  }
}
